<template lang="pug">
span
  component(:is="dropdown.isNested ? 'span' : 'control'")
    field(:class="{ invalid: !dropdown.meta.valid && dropdown.meta.touched }")
      label(:for="dropdown.id")(v-html="dropdown.label")
      div
        .dropdown(:class="{ open: dropdown.open }", ref="root")
          .dropdown__display(@click.stop="dropdown.toggle(!dropdown.open)")
            .dropdown__selections(
              ref="selections",
              tabindex="0",
              @keydown.self="dropdown.doSearch($event)",
              @keydown.space.self="dropdown.toggle(!dropdown.open)",
              @keydown.escape.self="dropdown.toggle(false)"
            )
              span(
                v-for="selection in dropdown.display",
                :class="{ tag: dropdown.multi }"
              ) {{ selection }}
            button(
              type="button",
              v-if="dropdown.display.length",
              @click.stop.prevent="dropdown.clear",
              @keydown.enter.stop.prevent="dropdown.clear"
            )
          .dropdown__options(
            @keydown="dropdown.doSearch($event, true)")
            .dropdown__option(
              v-for="option in dropdown.options",
              :class="{ selected: dropdown.display.includes(option[1]) }"
            )
              input(
                v-model="dropdown.model",
                :type="dropdown.multi ? 'checkbox' : 'radio'",
                :id="`${dropdown.id}-${option[0]}-${option[1]}`",
                :value="option[0]",
                :ref="option[2]",
                @keydown.enter.stop.prevent="$event.target.click()",
                @keydown.space.stop.prevent="$event.target.click()",
                @keydown.escape.stop.prevent="dropdown.toggle(false)",
                @focus="dropdown.scrollIntoView($event.target)"
              )
              label(:for="`${dropdown.id}-${option[0]}-${option[1]}`") {{ option[1] }}
      error(v-if="!dropdown.meta.valid && dropdown.meta.touched") {{ dropdown.errorMessage }}
</template>

<script>
import { useStore } from "vuex";
import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useField } from "vee-validate";
import { cloneDeep } from "lodash-es";
import * as yup from "yup";
export default {
  name: "Dropdown",
  props: {
    field: Object,
    nested: Boolean,
  },
  setup(p) {
    const store = useStore();
    const dropdown = reactive(initDropdown());
    const selections = ref(null);
    const root = ref(null);

    if (p.field.valueSource) {
      fetch(p.field.valueSource)
        .then((res) => res.json())
        .then((data) => {
          dropdown.options = data.map((opt) => ([opt[0], opt.length > 1 ? opt[1] : opt[0], ref(null)]));
          dropdown.loading = false;
        });
    }

    return { dropdown, selections, root };

    function initDropdown() {
      let labelHtml = p.field.label;
      if (labelHtml && p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = p.field.id;
      const label = labelHtml
        ? `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}`
        : null;
      const placeholder = "Select";
      const multi = p.field.multi;
      const mode = multi ? "multiple" : "single";
      const emptyText = "";
      const open = ref(false);
      const initialValue = store.getters.model[p.field.id]
        ? store.getters.model[p.field.id]
        : multi
          ? []
          : "";
      const searchString = ref("");
      const isNested = p.nested;
      let loading = false;
      let options = cloneDeep(p.field.values) || [];
      let rules;
      let searching = false;

      if (p.field.valueSource) {
        loading = true;
      } else {
        options = options.map((opt) => ([opt[0], opt.length > 1 ? opt[1] : opt[0], ref(null)]));
      }

      if (multi) {
        rules =
          p.field.required === false
            ? yup.array().min(0).nullable()
            : yup.array().min(1).required().nullable();
      } else {
        rules =
          p.field.required === false
            ? yup.string().nullable()
            : yup.string().required().nullable();
      }

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue,
        }
      );

      store.commit("updateModel", { id: p.field.id, value: initialValue });

      const model = computed({
        get: () => store.getters.model[p.field.id],
        set: (v) => {
          multi ? false : toggle(false);
          value.value = v;
          store.commit("updateModel", { id: p.field.id, value: v });
        },
      });

      watch(
        () => p.field.model,
        () => {
          model.value = p.field.model;
          toggle(false);
        }
      );

      const blur = async () => {
        store.commit("pushLastFieldToDataLayer", id);
        await validate();
        handleBlur();
      };

      const toggle = (state = !open.value) => {
        open.value = state;
        searchString.value = "";
        if (!open.value) {
          selections.value.focus();
          blur();
        }
      };

      const display = computed(() => {
        if (multi) {
          return options.reduce((selections, option) => {
            if (!model.value) return [];
            if (model.value.includes(option[0])) {
              selections.push(option[1]);
            }
            return selections;
          }, []);
        } else {
          if (!model.value) return [];
          let option = dropdown.options.find((option) => String(option[0]) === String(model.value));
          if (option) return [option[1]];
          return [];
        }
      });

      const clear = () => {
        model.value = multi ? [] : "";
      };

      let clearSearchDebounce;
      const clearSearch = () => {
        searchString.value = "";
      };

      const navigate = (event) => {
        event.stopPropagation();
        event.preventDefault();
        event.stopImmediatePropagation();
        const target = event.target;
        const container = target
          .closest(".dropdown")
          .querySelector(".dropdown__options");
        let label, option;
        let dir = event.key;
        if (!open.value) open.value = true;

        if (target === selections.value) {
          option = dir === 'ArrowUp' ? options[options.length - 1][2].value[0] : options[0][2].value[0];
          label = option.nextElementSibling;
        }
        else if (options.some(o => o[2].value[0] === target)) {
          option = target.parentElement[dir === 'ArrowUp' ? 'previousElementSibling' : 'nextElementSibling']?.firstElementChild;
          if (option) label = option.nextElementSibling;
        }
        if (option) {
          setTimeout(() => {
            option.focus();
            container.scrollTo({ top: label.offsetTop, behavior: "auto" });
          }, 50);
        }
        else {
          open.value = false;
          selections.value.focus();
        }
      }

      const doSearch = (event, stop = false) => {
        if (stop) event.stopPropagation();
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') return navigate(event);

        const char = String.fromCharCode(event.keyCode);
        if (!/^[A-Za-z]+$/.test(char)) return;
        searching = true;
        open.value = true;
        searchString.value += char.toLowerCase();

        const target = event.target;
        const container = target
          .closest(".dropdown")
          .querySelector(".dropdown__options");
        const options = [
          ...container.querySelectorAll(".dropdown__option label"),
        ];
        let option = options.find((o) => {
          let text = o.textContent.toLowerCase();
          return text.startsWith(searchString.value);
        });

        if (!option) {
          option = options.find((o) => {
            let text = o.previousElementSibling.value.toLowerCase();
            return text.startsWith(searchString.value);
          });
        }

        if (option) {
          option.previousElementSibling.focus();
          container.scrollTo({ top: option.offsetTop, behavior: "auto" });
          if (searching) clearTimeout(clearSearchDebounce);
          clearSearchDebounce = setTimeout(clearSearch, 750);
        } else {
          selections.value.focus();
        }
        searching = false;
      };

      const scrollIntoView = (target) => {
        const container = target.closest(".dropdown__options");
        const option = target.nextElementSibling;
        container.scrollTo({ top: option.offsetTop, behavior: "auto" });
      };

      const closer = (e) => {
        if (!open.value) return;
        let container = e.target.closest(".dropdown");
        if (!container || container !== root.value) {
          toggle(false);
        }
      };

      onMounted(() => {
        document.addEventListener("click", closer);
      });
      onBeforeUnmount(() => {
        document.removeEventListener("click", closer);
      });

      return {
        id,
        label,
        open,
        display,
        placeholder,
        loading,
        mode,
        multi,
        options,
        model,
        emptyText,
        isNested,
        meta,
        blur,
        searchString,
        toggle,
        clear,
        doSearch,
        errorMessage,
        scrollIntoView,
      };
    }
  },
};
</script>

<style lang="scss" scoped>
span > span {
  display: contents;
}
[class][class][class].dropdown {
  display: flex;
  flex-direction: column;
  position: relative;
  &:focus-within {
    .dropdown__display,
    .dropdown__options {
      box-shadow: 0 0 0 3px $sky;
    }
  }

  &.open .dropdown__display::before {
    opacity: 1;
  }
  &.invalid .dropdown__display::before {
    background: lighten($red, 57%);
  }
  * {
    &:focus {
      outline: none;
    }
  }

  &__display {
    display: flex;
    cursor: pointer;
    gap: 0.25rem;
    flex: 1 1 100%;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: calc(100% - 2px);
      left: -1px;
      right: -1px;
      height: 4px;
      background: $white;
      z-index: 2;
      border-left: 1px solid $slate;
      border-right: 1px solid $slate;
      pointer-events: none;
      transition: 0.2s ease;
      opacity: 0;
      border-top: 1px solid $slate;
    }

    input[type="text"] {
      all: unset;
      appearance: none;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: calc(100% - 2.5rem);
      height: 100%;
      box-sizing: border-box;
      cursor: pointer;
      color: transparent;
      &:focus {
        box-shadow: none;
      }
      .open & {
        pointer-events: none;
      }
    }

    button {
      all: unset;
      position: relative;
      appearance: none;
      border: none;
      padding: 0;
      cursor: pointer;
      flex: 0 0 auto;
      height: 1rem;
      width: 1rem;
      align-self: center;
      opacity: 0.5;
      transition: 0.2s ease;

      &:hover,
      &:focus {
        opacity: 1;
        box-shadow: 0 0 0 3px $sky;
      }

      &::before,
      &::after {
        content: "";
        height: 2px;
        width: calc(100% - 0.25rem);
        background: currentColor;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
      }
      &::after {
        transform: translate(-50%, -50%) rotate(-45deg);
      }
    }

    &::after {
      content: "";
      height: 0;
      width: 0;
      border-left: 0.3rem solid transparent;
      border-right: 0.3rem solid transparent;
      border-top: 0.35rem solid currentColor;
      border-radius: 3px;
      align-self: center;
    }
  }

  &__selections {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    flex: 1 1 100%;
    span {
      &.tag {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.25rem 0.3875rem;
        background: $gray;
        font-style: normal;
        white-space: nowrap;
        line-height: 1;
      }
    }
  }

  &__options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    border: 1px solid $slate;
    padding: 0.25rem 0;
    background: $white;
    z-index: 1;
    max-height: 0;
    visibility: hidden;
    overflow: hidden;
    opacity: 0;
    transition: 0.2s ease;

    .open & {
      max-height: 200px;
      overflow-y: auto;
      visibility: visible;
      opacity: 1;
    }
  }

  &__option {
    display: flex;
    flex-direction: column;
    label {
      all: unset;
      padding: 0.3875rem;
      transition: 0.2s ease;
      background: transparent;
      cursor: pointer;
      &:hover,
      &.selected {
        background: $gray;
        outline: none;
      }

      &::before {
        all: unset;
      }
    }

    &.selected {
      label {
        background: darken($gray, 3%);
        &:hover {
          outline: none;
          background: darken($gray, 5%);
        }
      }
    }

    input {
      &:focus + label {
        background: $gray;
        outline: none;

        &.selected {
          &:hover {
            outline: none;
            background: darken($gray, 5%);
          }
        }
      }
    }
  }
}
</style>
